import { createApi } from "../../../../utils/createApi";
import type { NavigateFunction } from "react-router-dom";

export type BulkMetricUpdateApiItem = {
  label_name: string;
  active: boolean | null;
  sensitivity_id: number | null;
  frame_thresh: number | null;
  min_crowd_size: number | null;
};

export type SensitivityLevels = {
  low?: number;
  medium?: number;
  medium_high?: number;
  high?: number;
};

/**
 * Maps a sensitivity option display name to its bulk-update level. Prod rows carry
 * qualifiers — "High (more sensitive)", "Intermediate (Medium-High)" — so an exact
 * low/medium/high match drops them, which made bulk High unresolvable and
 * Medium-High unavailable (SUP-440 / ENG-1430). Returns null for levels the bulk
 * form doesn't offer (e.g. Motion Plus's "Very Low").
 */
export const sensitivityLevelForOptionName = (
  rawName: string
): keyof SensitivityLevels | null => {
  const name = rawName.toLowerCase();
  if (name.includes("medium-high") || name.includes("medium high")) {
    return "medium_high";
  }
  // Strip parenthetical qualifiers: "High (more sensitive)" → "high".
  const base = name.replace(/\(.*?\)/g, "").trim();
  if (base === "low") return "low";
  if (base === "medium") return "medium";
  if (base === "high") return "high";
  return null;
};

/**
 * Maps metric API `options` arrays ([label, id]) to sensitivity-level ids per label_name.
 */
export const buildSensitivityMapFromMetricResults = (
  results: unknown[]
): Map<string, SensitivityLevels> => {
  const map = new Map<string, SensitivityLevels>();
  for (const raw of results) {
    const metric = raw as {
      label_name?: string;
      options?: unknown[];
    };
    const label = metric.label_name;
    if (!label || !Array.isArray(metric.options)) continue;
    const entry: SensitivityLevels = {};
    for (const opt of metric.options) {
      if (!Array.isArray(opt) || opt.length < 2) continue;
      const level = sensitivityLevelForOptionName(String(opt[0]));
      if (level !== null) {
        entry[level] = opt[1] as number;
      }
    }
    if (Object.keys(entry).length > 0) {
      map.set(label, entry);
    }
  }
  return map;
};

export const fetchMetricSensitivityMap = async (
  navigate: NavigateFunction,
  cameraId: number
): Promise<Map<string, SensitivityLevels>> => {
  const AxiosUI = createApi(``);
  const response = await AxiosUI.get(`/metric/?camera_id=${cameraId}`);
  return buildSensitivityMapFromMetricResults(response.data?.results || []);
};

/**
 * Flattens GET /camera/{id}/general_info/ streams[].stream_metrics into the same
 * label_name → sensitivity-id map. Unlike /metric/?camera_id= (which only returns
 * metrics that exist — disabling a product hard-deletes its row), general_info also
 * includes placeholder rows with `options` for every product the user could add,
 * so it resolves sensitivities for products currently disabled on the camera.
 */
export const buildSensitivityMapFromGeneralInfo = (
  generalInfo: unknown
): Map<string, SensitivityLevels> => {
  const streams = (generalInfo as { streams?: unknown[] })?.streams;
  if (!Array.isArray(streams)) {
    return new Map();
  }
  const metrics = streams.flatMap((stream) => {
    const streamMetrics = (stream as { stream_metrics?: unknown[] })
      ?.stream_metrics;
    return Array.isArray(streamMetrics) ? streamMetrics : [];
  });
  return buildSensitivityMapFromMetricResults(metrics);
};

export const fetchGeneralInfoSensitivityMap = async (
  navigate: NavigateFunction,
  cameraId: number
): Promise<Map<string, SensitivityLevels>> => {
  void navigate;
  const AxiosUI = createApi(``);
  const response = await AxiosUI.get(`/camera/${cameraId}/general_info/`);
  return buildSensitivityMapFromGeneralInfo(response.data);
};

/** Merges two sensitivity maps; entries (and individual levels) in `primary` win. */
export const mergeSensitivityMaps = (
  primary: Map<string, SensitivityLevels>,
  fallback: Map<string, SensitivityLevels>
): Map<string, SensitivityLevels> => {
  const merged = new Map(primary);
  fallback.forEach((levels, label) => {
    merged.set(label, { ...levels, ...(merged.get(label) ?? {}) });
  });
  return merged;
};

export const postMetricBulkUpdate = async (
  navigate: NavigateFunction,
  cameraIds: number[],
  bulkUpdate: BulkMetricUpdateApiItem[]
): Promise<{ status?: string; cameras_queued?: number }> => {
  void navigate;
  const AxiosUI = createApi(``);
  const response = await AxiosUI.post(`/metric/bulk_update/`, {
    camera_ids: cameraIds,
    bulk_update: bulkUpdate,
  });
  return response.data ?? {};
};

export type SimpleStreamCameraRef = {
  id: number;
  camera_name?: string;
};

/** One row from GET /stream/simple/?customer= */
export type SimpleStreamRow = {
  id: number;
  name?: string;
  camera?: number | SimpleStreamCameraRef;
  slice?: boolean;
  max_slices?: number | null;
};

export type StreamProductRow = {
  id: number;
  camera: number;
  name: string;
  product: string;
};

export const fetchStreamProductsForCamera = async (
  navigate: NavigateFunction,
  cameraId: number
): Promise<StreamProductRow[]> => {
  void navigate;
  const AxiosUI = createApi(``);
  const response = await AxiosUI.get(`/stream/product/?camera_id=${cameraId}`);
  const data = response.data;
  if (Array.isArray(data)) {
    return data as StreamProductRow[];
  }
  return [];
};

/**
 * Lists streams for a customer (used to resolve stream ids and current slice / max_slices).
 */
export const fetchStreamsSimple = async (
  navigate: NavigateFunction,
  customerId: number | string
): Promise<SimpleStreamRow[]> => {
  void navigate;
  const AxiosUI = createApi(``);
  const response = await AxiosUI.get(`/stream/simple/`, {
    params: { customer: customerId },
  });
  const data = response.data;
  if (Array.isArray(data)) {
    return data as SimpleStreamRow[];
  }
  if (Array.isArray(data?.results)) {
    return data.results as SimpleStreamRow[];
  }
  return [];
};

export type StreamBulkUpdatePayload = {
  stream_ids: number[];
  slice: boolean;
  /** Omit from POST body unless explicitly set (legacy / future use). */
  max_slices?: number | null;
};

export const postStreamBulkUpdate = async (
  navigate: NavigateFunction,
  payload: StreamBulkUpdatePayload
): Promise<unknown> => {
  void navigate;
  const AxiosUI = createApi(``);
  const body: Record<string, unknown> = {
    stream_ids: payload.stream_ids,
    slice: payload.slice,
  };
  if (payload.max_slices !== undefined) {
    body.max_slices = payload.max_slices;
  }
  const response = await AxiosUI.post(`/stream/bulk_update/`, body);
  return response.data;
};

export type MetricSimplifiedCameraRef = {
  id: number;
  camera_name?: string;
};

/** One row from GET /metric/simplified/?customer= */
export type MetricSimplifiedRow = {
  camera?: MetricSimplifiedCameraRef;
  metric_id: number;
  label_id: number;
  label_name: string;
  active: boolean;
  sensitivity_name?: string;
};

export const fetchMetricSimplified = async (
  navigate: NavigateFunction,
  customerId: number | string
): Promise<MetricSimplifiedRow[]> => {
  void navigate;
  const AxiosUI = createApi(``);
  const response = await AxiosUI.get(`/metric/simplified/`, {
    params: { customer: customerId },
  });
  const data = response.data;
  if (Array.isArray(data?.results)) {
    return data.results as MetricSimplifiedRow[];
  }
  if (Array.isArray(data)) {
    return data as MetricSimplifiedRow[];
  }
  return [];
};
